<template>
  <main class="admin-page">
    <section class="toolbar">
      <div>
        <h2>{{ isEn ? 'Admin Management' : '后台管理' }}</h2>
        <p>
          {{
            isEn
              ? 'View platform users. Click "View Data" to enter all data for that user.'
              : '查看平台用户，点击每个用户的“查看数据”进入该用户全部数据。'
          }}
        </p>
      </div>
      <el-button
        type="primary"
        icon="el-icon-plus"
        @click="openCreateDialog"
      >
        {{ isEn ? 'Add Account' : '新增账号' }}
      </el-button>
    </section>

    <el-table v-loading="loading" :data="users" border class="admin-table">
      <el-table-column prop="id" :label="isEn ? 'User ID' : '用户 ID'" width="90" />
      <el-table-column prop="username" :label="isEn ? 'Account' : '账号'" min-width="150" show-overflow-tooltip />
      <el-table-column prop="phone" :label="isEn ? 'Phone' : '手机号'" min-width="150" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ row.phone || '-' }}</template>
      </el-table-column>
      <el-table-column prop="email" :label="isEn ? 'Email' : '邮箱'" min-width="210" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ row.email || '-' }}</template>
      </el-table-column>
      <el-table-column :label="isEn ? 'Role' : '角色'" width="140">
        <template slot-scope="{ row }">
          <el-tag :type="row.role === 'super_admin' ? 'danger' : 'success'">
            {{ roleLabel(row) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="created_at" :label="isEn ? 'Registered At' : '注册时间'" min-width="180" show-overflow-tooltip />
      <el-table-column :label="isEn ? 'All Data' : '所有数据'" width="130" align="center">
        <template slot-scope="{ row }">
          <el-button size="mini" type="primary" @click="viewUserData(row)">
            {{ isEn ? 'View Data' : '查看数据' }}
          </el-button>
        </template>
      </el-table-column>
      <el-table-column :label="isEn ? 'Status' : '状态'" width="90">
        <template slot-scope="{ row }">{{ row.is_active ? (isEn ? 'Active' : '正常') : (isEn ? 'Disabled' : '停用') }}</template>
      </el-table-column>
      <el-table-column :label="isEn ? 'Action' : '操作'" width="210" align="center">
        <template slot-scope="{ row }">
          <el-button
            size="mini"
            :disabled="!canManage(row)"
            @click="openReset(row)"
          >
            {{ isEn ? 'Reset Password' : '重置密码' }}
          </el-button>
          <el-button
            size="mini"
            type="danger"
            :disabled="!canDelete(row)"
            @click="deleteUser(row)"
          >
            {{ isEn ? 'Delete' : '删除' }}
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog
      :title="isEn ? 'Add Account' : '新增账号'"
      :visible.sync="dialogVisible"
      width="520px"
      append-to-body
      :close-on-click-modal="false"
      @closed="resetCreateForm"
    >
      <el-form ref="adminForm" :model="form" :rules="rules" label-position="top">
        <el-form-item :label="isEn ? 'Role' : '角色'" prop="role">
          <el-select v-model="form.role" style="width: 100%">
            <el-option :label="isEn ? 'User' : '普通用户'" value="user" />
            <el-option
              :label="isEn ? 'Sub Admin' : '子管理员'"
              value="admin"
              :disabled="!currentUser.is_primary_admin"
            />
          </el-select>
        </el-form-item>
        <el-form-item :label="isEn ? 'Account' : '账号'" prop="username">
          <el-input v-model.trim="form.username" :placeholder="isEn ? 'Enter account' : '请输入账号'" />
        </el-form-item>
        <el-form-item :label="isEn ? 'Phone (optional)' : '手机号（选填）'" prop="phone">
          <el-input v-model.trim="form.phone" :placeholder="isEn ? 'Enter phone number' : '请输入手机号'" />
        </el-form-item>
        <el-form-item :label="isEn ? 'Email (optional)' : '邮箱（选填）'" prop="email">
          <el-input v-model.trim="form.email" :placeholder="isEn ? 'Enter email' : '请输入邮箱'" />
        </el-form-item>
        <el-form-item :label="isEn ? 'Initial Password' : '初始密码'" prop="password">
          <el-input
            v-model="form.password"
            type="password"
            show-password
            :placeholder="isEn ? 'At least 6 characters' : '至少 6 位'"
          />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">{{ isEn ? 'Cancel' : '取消' }}</el-button>
        <el-button type="primary" :loading="saving" @click="createAccount">{{ isEn ? 'Create' : '创建' }}</el-button>
      </span>
    </el-dialog>

    <el-dialog
      :title="isEn ? 'Reset Password' : '重置密码'"
      :visible.sync="resetVisible"
      width="420px"
      append-to-body
      :close-on-click-modal="false"
    >
      <el-form ref="resetForm" :model="resetForm" :rules="resetRules" label-position="top">
        <el-form-item :label="isEn ? 'Account' : '账号'">
          <el-input :value="resetTarget ? resetTarget.username : ''" disabled />
        </el-form-item>
        <el-form-item :label="isEn ? 'New Password' : '新密码'" prop="password">
          <el-input
            v-model="resetForm.password"
            type="password"
            show-password
            :placeholder="isEn ? 'At least 6 characters' : '至少 6 位'"
          />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="resetVisible = false">{{ isEn ? 'Cancel' : '取消' }}</el-button>
        <el-button type="primary" :loading="saving" @click="resetPassword">{{ isEn ? 'Save' : '保存' }}</el-button>
      </span>
    </el-dialog>
  </main>
</template>

<script>
export default {
  name: 'AdminUsers',
  data() {
    return {
      loading: false,
      saving: false,
      dialogVisible: false,
      users: [],
      currentUser: {},
      form: { role: 'user', username: '', phone: '', email: '', password: '' },
      resetVisible: false,
      resetTarget: null,
      resetForm: { password: '' }
    }
  },
  computed: {
    isEn() {
      return this.$i18n.locale === 'en'
    },
    rules() {
      return {
        role: [
          { required: true, message: this.isEn ? 'Select a role' : '请选择角色', trigger: 'change' }
        ],
        username: [
          { required: true, message: this.isEn ? 'Enter account' : '请输入账号', trigger: 'blur' },
          { min: 3, max: 64, message: this.isEn ? 'Account length must be 3 to 64 characters' : '账号长度为 3 到 64 位', trigger: 'blur' }
        ],
        phone: [
          { pattern: /^\+?[0-9\s()-]{6,24}$/, message: this.isEn ? 'Invalid phone number' : '手机号格式不正确', trigger: 'blur' }
        ],
        email: [
          { type: 'email', message: this.isEn ? 'Invalid email address' : '邮箱格式不正确', trigger: 'blur' }
        ],
        password: [
          { required: true, message: this.isEn ? 'Enter initial password' : '请输入初始密码', trigger: 'blur' },
          { min: 6, max: 128, message: this.isEn ? 'Password must be at least 6 characters' : '密码至少 6 位', trigger: 'blur' }
        ]
      }
    },
    resetRules() {
      return {
        password: [
          { required: true, message: this.isEn ? 'Enter new password' : '请输入新密码', trigger: 'blur' },
          { min: 6, max: 128, message: this.isEn ? 'Password must be at least 6 characters' : '密码至少 6 位', trigger: 'blur' }
        ]
      }
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    roleLabel(row) {
      if (row.is_primary_admin) return this.isEn ? 'Primary Admin' : '主管理员'
      if (row.role === 'super_admin') return this.isEn ? 'Sub Admin' : '子管理员'
      return this.isEn ? 'User' : '普通用户'
    },
    openCreateDialog() {
      this.resetCreateForm()
      this.dialogVisible = true
    },
    resetCreateForm() {
      this.form = { role: 'user', username: '', phone: '', email: '', password: '' }
      this.$nextTick(() => {
        if (this.$refs.adminForm) this.$refs.adminForm.clearValidate()
      })
    },
    async loadData() {
      this.loading = true
      try {
        const base = process.env.VUE_APP_AUTH_BASE_URL || 'http://localhost:4201/auth'
        const [me, users] = await Promise.all([
          this.$http.get(`${base}/me`),
          this.$http.get(`${base}/users`)
        ])
        if (me.data.role !== 'super_admin') {
          this.$message.error(this.isEn ? 'No admin access permission' : '无后台访问权限')
          this.$router.push('/')
          return
        }
        this.currentUser = me.data
        this.users = users.data
      } catch (error) {
        this.$message.error(error.response?.data?.detail || (this.isEn ? 'Failed to load accounts' : '加载账号失败'))
      } finally {
        this.loading = false
      }
    },
    viewUserData(row) {
      this.$router.push({
        path: '/results',
        query: { owner_id: row.id, owner_name: row.username }
      })
    },
    canManage(row) {
      if (!row || row.is_primary_admin) return false
      if (row.role === 'super_admin') return !!this.currentUser.is_primary_admin
      return true
    },
    canDelete(row) {
      return this.canManage(row) && row.id !== this.currentUser.id && row.is_active
    },
    createAccount() {
      this.$refs.adminForm.validate(async valid => {
        if (!valid) return
        this.saving = true
        try {
          const base = process.env.VUE_APP_AUTH_BASE_URL || 'http://localhost:4201/auth'
          await this.$http.post(`${base}/users`, {
            role: this.form.role,
            username: this.form.username,
            phone: this.form.phone || null,
            email: this.form.email || null,
            password: this.form.password
          })
          this.$message.success(this.isEn ? 'Account created' : '账号创建成功')
          this.dialogVisible = false
          await this.loadData()
        } catch (error) {
          this.$message.error(error.response?.data?.detail || (this.isEn ? 'Create failed' : '创建失败'))
        } finally {
          this.saving = false
        }
      })
    },
    openReset(row) {
      this.resetTarget = row
      this.resetForm = { password: '' }
      this.resetVisible = true
    },
    resetPassword() {
      this.$refs.resetForm.validate(async valid => {
        if (!valid || !this.resetTarget) return
        this.saving = true
        try {
          const base = process.env.VUE_APP_AUTH_BASE_URL || 'http://localhost:4201/auth'
          await this.$http.post(`${base}/users/${this.resetTarget.id}/reset-password`, this.resetForm)
          this.$message.success(this.isEn ? 'Password reset' : '密码已重置')
          this.resetVisible = false
        } catch (error) {
          this.$message.error(error.response?.data?.detail || (this.isEn ? 'Reset failed' : '重置失败'))
        } finally {
          this.saving = false
        }
      })
    },
    async deleteUser(row) {
      try {
        await this.$confirm(
          this.isEn ? `Delete account ${row.username}?` : `确定删除账号 ${row.username} 吗？`,
          this.isEn ? 'Notice' : '提示',
          {
            type: 'warning',
            confirmButtonText: this.isEn ? 'OK' : '确定',
            cancelButtonText: this.isEn ? 'Cancel' : '取消'
          }
        )
        const base = process.env.VUE_APP_AUTH_BASE_URL || 'http://localhost:4201/auth'
        await this.$http.delete(`${base}/users/${row.id}`)
        this.$message.success(this.isEn ? 'Account deleted' : '账号已删除')
        await this.loadData()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error(error.response?.data?.detail || (this.isEn ? 'Delete failed' : '删除失败'))
        }
      }
    }
  }
}
</script>

<style scoped>
.admin-page {
  max-width: 1180px;
  margin: 0 auto 48px;
  padding: 0 28px;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 20px;
}

.toolbar h2 {
  margin: 0 0 6px;
  color: #10233f;
}

.toolbar p {
  margin: 0;
  color: #607086;
}

.admin-table {
  width: 100%;
}

@media (max-width: 900px) {
  .admin-page {
    padding: 0 14px;
  }
}

@media (max-width: 700px) {
  .toolbar {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
